import { Member, MemberContactMethod, MemberPersonalDetails, Subscription } from '@vista-digital/ocapi-types/v1'
import {
    getMember,
    getSubscriptionById,
    IndexKey,
    loadMember as loadMemberOcc,
    updateMemberPersonalDetails,
} from '@vista-digital/occ-domain'
import { MemberSubscriptionubscriptionDetails } from '../components'
import { api, cmsDataService } from '../services'

export async function loadMember(): Promise<Member | undefined> {
    try {
        const dontReloadCachedData = false
        const member = getMember() ?? (await loadMemberOcc(dontReloadCachedData))
        return member
    } catch (e) {
        return undefined
    }
}

export async function updateMemberDetailsFromSignUp(
    preferences: MemberContactMethod[],
    hasChildren: IndexKey
): Promise<void> {
    const member = await loadMember()
    if (!member) return

    const newDetails: MemberPersonalDetails = {
        ...member.personalDetails,
        householdPersonCount: +hasChildren,
        preferences: {
            ...member.personalDetails.preferences,
            contact: {
                ...member.personalDetails.preferences.contact,
                contactMethods: preferences,
            },
        },
    }

    // TODO: CAPTCHA REQUIRED HERE POSSIBLE
    await updateMemberPersonalDetails(newDetails, undefined)
}

export async function updateMemberContactMethods(preferences: MemberContactMethod[]): Promise<boolean> {
    const member = await loadMember()
    if (!member) return

    const newDetails: MemberPersonalDetails = {
        ...member.personalDetails,
        preferences: {
            ...member.personalDetails.preferences,
            contact: {
                ...member.personalDetails.preferences.contact,
                contactMethods: preferences,
            },
        },
    }

    try {
        // TODO: CAPTCHA REQUIRED HERE POSSIBLE
        await updateMemberPersonalDetails(newDetails, undefined)
        return true
    } catch (err) {
        return false
    }
}

export async function updateMemberGenres(genres: string[]): Promise<boolean> {
    const member = await loadMember()
    if (!member) return

    const newDetails: MemberPersonalDetails = {
        ...member.personalDetails,
        preferences: {
            ...member.personalDetails.preferences,
            genres: {
                ...member.personalDetails.preferences.genres,
                preferredGenreIds: genres,
            },
        },
    }

    try {
        // TODO: CAPTCHA REQUIRED HERE POSSIBLE
        await updateMemberPersonalDetails(newDetails, undefined)
        return true
    } catch (err) {
        return false
    }
}

export async function updateMemberCinemas(cinemaOne: string, cinemaTwo: string, cinemaThree: string): Promise<boolean> {
    const member = await loadMember()
    if (!member) return

    const sites: string[] = []
    if (!!cinemaOne && cinemaOne !== '0') sites.push(cinemaOne)
    if (!!cinemaTwo && cinemaTwo !== '0') sites.push(cinemaTwo)
    if (!!cinemaThree && cinemaThree !== '0') sites.push(cinemaThree)

    const primarySite = !!cinemaOne && cinemaOne !== '0' ? cinemaOne : null

    const newDetails: MemberPersonalDetails = {
        ...member.personalDetails,
        preferences: {
            ...member.personalDetails.preferences,
            sites: {
                ...member.personalDetails.preferences.sites,
                primarySiteId: primarySite,
                preferredSiteIds: sites,
            },
        },
    }

    try {
        // TODO: CAPTCHA REQUIRED HERE POSSIBLE
        await updateMemberPersonalDetails(newDetails, undefined)
        return true
    } catch (err) {
        return false
    }
}

export async function getMemberContactMethods(): Promise<MemberContactMethod[]> {
    const member = await loadMember()
    if (!member) return []
    return member.personalDetails.preferences.contact.contactMethods
}

export async function getMemberPreferredGenres(): Promise<string[]> {
    const member = await loadMember()
    if (!member) return []

    return member.personalDetails.preferences.genres.preferredGenreIds
}

export async function getMemberFavouriteCinemas(): Promise<{ primary: string | null; all: string[] }> {
    const member = await loadMember()
    if (!member) return { primary: null, all: [] }

    let sitePrefs = member.personalDetails.preferences.sites
    return { primary: sitePrefs.primarySiteId, all: sitePrefs.preferredSiteIds }
}

export async function getMemberSubscriptionDetails(): Promise<MemberSubscriptionsubscriptionDetails> {
    const member = await loadMember()
    if (!member) return

    let dates: MemberSubscriptionsubscriptionDetails = {
        memberSince: member.membershipStartDate,
    }

    let currentSubscription = null

    if (member.subscriptions) {
        currentSubscription = member.subscriptions.current

        const subscription: Subscription = getSubscriptionById(currentSubscription.id)

        dates = {
            id: currentSubscription.id,
            type: subscription.name,
            status: currentSubscription.status,
            memberSince: member.membershipStartDate,
        }
    }

    return dates
}

export async function getMemberSubscriptionExpiryDate(): Promise<Date | undefined> {
    try {
        const member = await loadMember()
        if (!member) return

        const [result, statusCode] = await api.GET<{
            loyaltyMember: {
                expiryDate: Date
            }
        }>(
            api.GetLoyaltyMember(),
            {
                Authorization: `Bearer ${cmsDataService.get().api.authToken}`,
            },
            true // Include auth cookies when sending to non origin domain for connect
        )

        if (statusCode < 200 || statusCode >= 300) {
            return null
        }

        return result?.loyaltyMember?.expiryDate
    } catch (err) {
        console.log(err)
        return null
    }
}

export async function testingMember() {
    const member = await loadMember()

    console.log('testing member', member)
}
